import React, { Suspense, lazy } from "react";
import Clarity from "@microsoft/clarity";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useNavigate,
} from "react-router-dom";
import Login from "./pages/login";
import ForgetPassword from "./pages/forget-password";
import SignUp from "./pages/sign-up";
import PrivateRoute from "./routers/private-router";
import AdminRoute from "./routers/admin-router";
import Landing from "./pages/landing";
import NotFound from "./pages/not-found";
import { Toaster } from "react-hot-toast";
import Admin from "./pages/admin";
import { Layout } from "./components/layout";
import Profile from "./pages/profile";
import FighterRegister from "./pages/fighter-register";
import ManageFighters from "./pages/manage-fighters";
import ManageEvents from "./pages/manage-events";
import ManageEvent from "./pages/manage-event";
import Lenis from "lenis";
import Event from "./pages/event";
import Fighter from "./pages/fighter";
import FighterConsentAndResponsibilityAgreement from "./pages/fighter-consent-and-responsibility-agreement";
import Rules from "./pages/rules";
import ManageFighterRequests from "./pages/manage-fighter-requests";
import ManageFighterRequest from "./pages/manage-fighter-request";
import RegisterEvent from "./pages/register-event";
import FighterEdit from "./pages/fighter-edit";
import ManageLiveStreamTickets from "./pages/manage-live-stream-tickets";
import ManageLiveStreamTicket from "./pages/manage-live-stream-ticket";
import About from "./pages/about";
import "./i18n"; // Import i18n configuration
import ResetPassword from "./pages/reset-password";
import EmailVerification from "./pages/email-verification";
import LiveStream from "./pages/live-stream";
import PurchaseTicket from "./pages/purchase-ticket";
import PrivacyPolicy from "./pages/privacy-policy";
import TermsOfService from "./pages/terms-of-service";
import ReturnAndRefundPolicy from "./pages/return-and-refund-policy";
import ActionHandler from "./pages/action-handler";
import FiuuReturn from "./pages/fiuu-return";
import PublicLayout from "./pages/public-layout";
import ManageOrders from "./pages/managa-orders";
import DeleteUser from "./pages/delete-user";
import CreateFighterAccount from "./pages/create-fighter-account";
import ErrorLogs from "./pages/error-logs";
import Judge from "./pages/judge";
import PartnerApi from "./pages/partner-api";
// Only around the results dashboard route: a chunk that fails to download
// (or a render error) shows a message and Reload instead of a blank tab.
import ResultsRouteBoundary from "./components/event-results/route-boundary";
// Loaded on demand: only admins open it, so the public site doesn't carry
// its code or its stylesheet.
const EventResults = lazy(() => import("./pages/event-results"));

Clarity.init("w9yeqt1iv0");

export const LANDING_PATH = "/";
export const ABOUT_PATH = "/about";
export const EVENT_PATH = "/event/:id";
export const FIGHTER_PATH = "/fighter/:id";
export const FIGHTER_CONSENT_AND_RESPONSIBILITY_AGREEMENT_PATH =
    "/fighter-consent-and-responsibility-agreement";
export const LIVE_STREAM_PATH = "/live-stream";
export const PURCHASE_TICKET_PATH = "/purchase-ticket";
export const PRIVACY_POLICY_PATH = "/privacy-policy";
export const TERMS_OF_SERVICE_PATH = "/terms-of-service";
export const RETURN_AND_REFUND_POLICY_PATH = "/return-and-refund-policy";
export const RULES_PATH = "/rules";
// Judge scoring page (token + PIN, no account). Registered outside the public
// and admin layouts: it has its own logo-only top bar.
export const JUDGE_PATH = "/judge/:token";

export const LOGIN_PATH = "/login";
export const SIGN_UP_PATH = "/sign-up";
export const FORGET_PASSWORD_PATH = "/forget-password";
export const EMAIL_VERIFICATION_PATH = "/email-verification";
export const RESET_PASSWORD_PATH = "/reset-password";
export const ACTION_HANDLER_PATH = "/action-handler";

export const PROFILE_PATH = "/profile";
export const FIGHTER_REGISTER_PATH = "/fighter-register";
export const FIGHTER_EDIT_PATH = "/fighter-edit";
export const REGISTER_EVENT_PATH = "/register-event";

export const ADMIN_PATH = "/admin";
export const MANAGE_FIGHTER_REQUESTS_PATH = "/manage-fighter-requests";
export const MANAGE_FIGHTER_REQUEST_PATH = "/manage-fighter-request";
export const MANAGE_FIGHTERS_PATH = "/manage-fighters";
export const MANAGE_EVENTS_PATH = "/manage-events";
export const MANAGE_LIVE_STREAM_TICKETS_PATH = "/manage-live-stream-tickets";
export const MANAGE_LIVE_STREAM_TICKET_PATH = "/manage-live-stream-ticket";
export const MANAGE_ORDERS_PATH = "/manage-orders";
export const DELETE_USER_PATH = "/delete-user";
export const CREATE_FIGHTER_ACCOUNT_PATH = "/create-fighter-account";
export const ERROR_LOGS_PATH = "/error-logs";
export const PARTNER_API_PATH = "/partner-api";

export const CREATE_EVENT_PATH = "/create-event";
export const MANAGE_EVENT_PATH = "/manage-event";
// The live results dashboard for one event (/event-results/:id). Admin only,
// opened in its own tab from Manage Event; no side menu (not under Layout).
export const EVENT_RESULTS_PATH = "/event-results";
export const ADMIN_USERS_PATH = "/admin-users";

export const FIUU_RETURN_PATH = "/fiuu-return";

function App() {
    return (
        <>
            <Router>
                <Routes>
                    <Route element={<PublicLayout />}>
                        <Route path={LANDING_PATH} element={<Landing />} />
                        <Route
                            path={FORGET_PASSWORD_PATH}
                            element={<ForgetPassword />}
                        />
                        <Route path={ABOUT_PATH} element={<About />} />
                        <Route path={EVENT_PATH} element={<Event />} />
                        <Route path={FIGHTER_PATH} element={<Fighter />} />
                        <Route path={LOGIN_PATH} element={<Login />} />
                        <Route path={SIGN_UP_PATH} element={<SignUp />} />
                        <Route
                            path={
                                FIGHTER_CONSENT_AND_RESPONSIBILITY_AGREEMENT_PATH
                            }
                            element={
                                <FighterConsentAndResponsibilityAgreement />
                            }
                        />
                        <Route
                            path={EMAIL_VERIFICATION_PATH}
                            element={<EmailVerification />}
                        />
                        <Route
                            path={RESET_PASSWORD_PATH}
                            element={<ResetPassword />}
                        />
                        <Route
                            path={ACTION_HANDLER_PATH}
                            element={<ActionHandler />}
                        />
                        <Route
                            path={PRIVACY_POLICY_PATH}
                            element={<PrivacyPolicy />}
                        />
                        <Route
                            path={TERMS_OF_SERVICE_PATH}
                            element={<TermsOfService />}
                        />
                        <Route
                            path={RETURN_AND_REFUND_POLICY_PATH}
                            element={<ReturnAndRefundPolicy />}
                        />
                        <Route
                            path={RULES_PATH}
                            element={<Rules />}
                        />
                        <Route
                            path={`${LIVE_STREAM_PATH}/:id`}
                            element={<LiveStream />}
                        />
                        <Route
                            path={`${PURCHASE_TICKET_PATH}/:id`}
                            element={<PurchaseTicket />}
                        />
                        <Route
                            path={FIUU_RETURN_PATH}
                            element={<FiuuReturn />}
                        />
                    </Route>

                    <Route element={<PrivateRoute />}>
                        <Route element={<PublicLayout />}>
                            <Route path={PROFILE_PATH} element={<Profile />} />
                            <Route
                                path={FIGHTER_REGISTER_PATH}
                                element={<FighterRegister />}
                            />
                            <Route
                                path={`${FIGHTER_REGISTER_PATH}/:id`}
                                element={<FighterRegister />}
                            />
                            <Route
                                path={`${FIGHTER_EDIT_PATH}/:id`}
                                element={<FighterEdit />}
                            />
                            <Route
                                path={`${REGISTER_EVENT_PATH}/:id`}
                                element={<RegisterEvent />}
                            />
                        </Route>
                    </Route>

                    <Route element={<AdminRoute />}>
                        <Route path="/" element={<Layout />}>
                            <Route
                                path={ADMIN_PATH.replace("/", "")}
                                element={<Admin />}
                            />
                            <Route
                                path={MANAGE_FIGHTERS_PATH.replace("/", "")}
                                element={<ManageFighters />}
                            />
                            <Route
                                path={MANAGE_EVENTS_PATH.replace("/", "")}
                                element={<ManageEvents />}
                            />
                            <Route
                                path={CREATE_EVENT_PATH.replace("/", "")}
                                element={<ManageEvent />}
                            />
                            <Route
                                path={`${MANAGE_EVENT_PATH.replace("/", "")}`}
                                element={<ManageEvent />}
                            />
                            <Route
                                path={`${MANAGE_EVENT_PATH.replace(
                                    "/",
                                    ""
                                )}/:id`}
                                element={<ManageEvent />}
                            />
                            <Route
                                path={MANAGE_FIGHTER_REQUESTS_PATH.replace(
                                    "/",
                                    ""
                                )}
                                element={<ManageFighterRequests />}
                            />
                            <Route
                                path={`${MANAGE_FIGHTER_REQUEST_PATH.replace(
                                    "/",
                                    ""
                                )}`}
                                element={<ManageFighterRequest />}
                            />
                            <Route
                                path={`${MANAGE_FIGHTER_REQUEST_PATH.replace(
                                    "/",
                                    ""
                                )}/:id`}
                                element={<ManageFighterRequest />}
                            />

                            <Route
                                path={MANAGE_LIVE_STREAM_TICKETS_PATH.replace(
                                    "/",
                                    ""
                                )}
                                element={<ManageLiveStreamTickets />}
                            />
                            <Route
                                path={`${MANAGE_LIVE_STREAM_TICKET_PATH.replace(
                                    "/",
                                    ""
                                )}/:id`}
                                element={<ManageLiveStreamTicket />}
                            />

                            <Route
                                path={MANAGE_ORDERS_PATH.replace("/", "")}
                                element={<ManageOrders />}
                            />

                            <Route
                                path={DELETE_USER_PATH.replace("/", "")}
                                element={<DeleteUser />}
                            />

                            <Route
                                path={CREATE_FIGHTER_ACCOUNT_PATH.replace("/", "")}
                                element={<CreateFighterAccount />}
                            />
                            <Route
                                path={ERROR_LOGS_PATH.replace("/", "")}
                                element={<ErrorLogs />}
                            />
                            <Route
                                path={PARTNER_API_PATH.replace("/", "")}
                                element={<PartnerApi />}
                            />
                        </Route>
                        <Route
                            path={`${EVENT_RESULTS_PATH}/:id`}
                            element={
                                <ResultsRouteBoundary>
                                    <Suspense
                                        fallback={
                                            <div className="w-full h-screen flex-center">
                                                <div className="loading loading-bars loading-lg" />
                                            </div>
                                        }
                                    >
                                        <EventResults />
                                    </Suspense>
                                </ResultsRouteBoundary>
                            }
                        />
                    </Route>

                    <Route path={JUDGE_PATH} element={<Judge />} />

                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Router>
            <Toaster
                position="bottom-right"
                toastOptions={{
                    className: "!rounded-none p-1 border-base-100",
                }}
            />
        </>
    );
}

export default App;
